import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { type TableColumnsType, Spin, Pagination, notification } from "antd";
import { useTranslation } from "react-i18next";

import { StyledThreeDotsVertical } from "./style";
import { useAppDispatch, useAppSelector } from "../../../../../../store/config";
import { fetchExercisesStart } from "../../../../../../store/features/exercises/exercises-slice";
import {
  deleteExerciseStart,
} from "../../../../../../store/features/exercise/exercise-slice";
import { IExercise } from "../../../../../../interfaces/store/exercise-interface";
import { useDebounce } from "../../../../../../hooks/useDebounce";
import {
  IMAGE_BASE_URL,
  PAGE_SIZE,
  VIDEO_BASE_URL,
} from "../../../../../../common/config";
import DeleteModal from "../../../../../../components/common/DeleteModal";
import { capitalizeFirstLetter } from "../../../../../../utils/utils";
import { SortType } from "../../../../../../interfaces/screens/fetch-params-types";
import {
  StyledExtendedCellContent,
  StyledExtendedCellImage,
  StyledExtendedCellTitle,
  StyledExtendedTableCell,
  StyledTable,
  TableRowActionsButton,
} from "../../../../../../components/common/styles/tableStyles";
import { TableHeader } from "../../../../../../components/table/TableHeader";
import { IFetchParams } from "../../../../../../interfaces/base";

export const ExercisesTable = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [api, contextHolder] = notification.useNotification();
  const { id: trainerId } = useParams<{ id: string }>();

  const [openDeleteModal, setOpenDeleteModal] = useState<boolean>(false);
  const [currentExerciseId, setCurrentExerciseId] = useState<string>("");
  const [deleteConfirmLoading, setDeleteConfirmLoading] =
    useState<boolean>(false);

  const { exercises, loading, error, metadata, tableState } = useAppSelector(
    (state) => state.exercises
  );

  const { search, sort, pagination } = tableState;
  const [inputSearch, setInputSearch] = useState<string>(search);

  const debouncedSearch = useDebounce(inputSearch, 300);

  const {
    error: deleteError,
    operation: deleteOperation,
    requestState: deleteRequestState,
  } = useAppSelector((state) => state.exercise);

  useEffect(() => {
    const paginationToUse =
      debouncedSearch !== search
        ? { page: 0, limit: PAGE_SIZE, skip: 0 }
        : pagination;

    fetchExercises({ pagination: paginationToUse });
  }, [debouncedSearch]);

  useEffect(() => {
    if (error || deleteError) {
      openNotification();
    }
  }, [error, deleteError]);

  useEffect(() => {
    if (!openDeleteModal) {
      setCurrentExerciseId("");
    }
  }, [openDeleteModal]);

  useEffect(() => {
    if (deleteRequestState === "finish" && deleteOperation === "delete") {
      setDeleteConfirmLoading(false);
      setOpenDeleteModal(false);
      fetchExercises({});
    }
  }, [deleteRequestState, deleteOperation]);

  const openNotification = () => {
    api["error"]({
      message: t("Something went wrong, please try again"),
      placement: "bottomLeft",
    });
  };

  const actionsMenuItems = [
    {
      key: "Edit",
      label: t("Edit"),
      onClick: (record: any) =>
        navigate(`${record._id}`, { state: { isEdit: true } }),
    },
    {
      key: "Delete",
      label: t("Delete"),
      onClick: (record: any) => {
        setCurrentExerciseId(record._id);
        setOpenDeleteModal(true);
      },
    },
  ];

  const columns: TableColumnsType<any> = useMemo(
    () => [
      {
        title: t("Exercise Name"),
        dataIndex: "name",
        render: (name: string, record: IExercise) => {
          const videoName = record.video?.replace(/\.mp4$/, "") || "";

          const imgSrc = record.image
            ? `${IMAGE_BASE_URL}/${record.image}`
            : videoName
            ? `${VIDEO_BASE_URL}/${videoName}__thumb_regular.jpg`
            : undefined;

          return (
            <StyledExtendedTableCell>
              <StyledExtendedCellImage src={imgSrc} />
              <StyledExtendedCellContent>
                <StyledExtendedCellTitle>{name}</StyledExtendedCellTitle>
              </StyledExtendedCellContent>
            </StyledExtendedTableCell>
          );
        },
      },
      {
        title: t("Difficulty"),
        dataIndex: "difficulty",
        render: (difficulty: string) => t(difficulty),
      },
      {
        title: t("Muscles"),
        dataIndex: "muscles",
        render: (muscles: string[]) => {
          const formattedMuscles = muscles
            ?.map((muscle) => t(capitalizeFirstLetter(muscle)))
            .join(", ");
          return t(formattedMuscles);
        },
      },
      {
        title: t("Time in Mins"),
        dataIndex: "time",
      },
      {
        title: "",
        dataIndex: "actions",
        render: (_: any, record: any) => (
          <TableRowActionsButton
            menu={{
              items: actionsMenuItems?.map((item) => ({
                key: item.key,
                label: item.label,
                onClick: () => item.onClick(record),
              })),
            }}
            trigger={["click"]}
          >
            <StyledThreeDotsVertical />
          </TableRowActionsButton>
        ),
      },
    ],
    [actionsMenuItems, t]
  );

  const onPaginationChange = (page: number) => {
    const newPagination = {
      ...pagination,
      page,
      skip: PAGE_SIZE * (page - 1),
    };

    fetchExercises({ pagination: newPagination });
  };

  const handleSetSort = (value: SortType) => {
    fetchExercises({
      sort: value,
      pagination: { page: 0, limit: PAGE_SIZE, skip: 0 },
    });
  };

  const handleDelete = () => {
    setDeleteConfirmLoading(true);
    dispatch(deleteExerciseStart({ exerciseId: currentExerciseId }));
  };

  const handleCreateNewExercise = () => {
    navigate(`/trainers/${trainerId}/exercises/add`);
  };

  const fetchExercises = (body: IFetchParams) => {
    dispatch(
      fetchExercisesStart({
        search: body.search || debouncedSearch,
        sort: body.sort || sort,
        pagination: body.pagination || pagination,
        filter: { trainer: trainerId },
      })
    );
  };

  return (
    <>
      {contextHolder}
      <TableHeader
        search={inputSearch}
        setSearch={setInputSearch}
        setSort={handleSetSort}
        handleCreateNew={handleCreateNewExercise}
        createNewText="Create New Exercise"
        currentSortBy={sort?.sortBy || ""}
      />
      <Spin spinning={loading}>
        <StyledTable
          columns={columns}
          dataSource={exercises}
          pagination={false}
          rowKey="_id"
          locale={{ emptyText: t("Empty") }}
        />
        <Pagination
          current={metadata?.page}
          pageSize={PAGE_SIZE}
          total={metadata?.total}
          align="center"
          hideOnSinglePage
          onChange={onPaginationChange}
          showSizeChanger={false}
          style={{ direction: "ltr", marginTop: "60px" }}
        />
        <DeleteModal
          open={openDeleteModal}
          setOpen={setOpenDeleteModal}
          handleOk={handleDelete}
          confirmLoading={deleteConfirmLoading}
        />
      </Spin>
    </>
  );
};
